import classnames from "classnames";
import { isPresent } from "neetocist";
import withT from "neetocommons/react-utils/withT";
import { dateFormat } from "neetocommons/utils";
import { ExternalLink, Delete, Clock } from "neetoicons";
import { Button, Tooltip, Typography } from "neetoui";
import PropTypes from "prop-types";
import { omit } from "ramda";
import { useTranslation } from "react-i18next";

import AlertBlock from "./AlertBlock";
import "./publish-block.scss";
import PublishSchedulePane from "./PublishSchedulePane";

const unUsedI18nProps = ["tReady", "i18n"];

const ViewDraftButton = withT(({ t, ...props }) => (
  <Button
    data-testid="view-draft-button"
    icon={ExternalLink}
    style="secondary"
    target="_blank"
    tooltipProps={{
      content: t("neetoMolecules.publishBlock.viewDraftVersion"),
      position: "top",
      touch: ["hold", 500],
    }}
    {...omit(unUsedI18nProps, props)}
  />
));

const ResetDraftButton = withT(({ t, ...props }) => (
  <Button
    data-testid="draft-button"
    icon={Delete}
    style="secondary"
    tooltipProps={{
      content: t("neetoMolecules.publishBlock.deleteDraftVersion"),
      position: "top",
      touch: ["hold", 500],
    }}
    {...omit(unUsedI18nProps, props)}
  />
));

const PublishButton = withT(
  ({
    t,
    tooltipProps,
    showTooltipWhenDisabled = false,
    disabled = false,
    ...props
  }) => (
    <Tooltip
      {...tooltipProps}
      disabled={!tooltipProps || (!showTooltipWhenDisabled && disabled)}
    >
      <div>
        <Button
          data-testid="publish-button"
          label={t("neetoMolecules.common.actions.publish")}
          {...{ disabled, ...omit(unUsedI18nProps, props) }}
        />
      </div>
    </Tooltip>
  )
);

const PublishPreviewButton = withT(({ t, ...props }) => (
  <Button
    data-testid="publish-preview-button"
    icon={ExternalLink}
    target="_blank"
    className={classnames(
      { "pointer-events-none": props.disabled },
      props.className
    )}
    tooltipProps={{
      content: t("neetoMolecules.publishBlock.viewPublishedVersion"),
      position: "top",
      touch: ["hold", 500],
    }}
    {...omit(unUsedI18nProps, props)}
  />
));

const PublishBlock = ({
  renderDraftButtons,
  renderPublishButtons,
  datetime,
}) => {
  const { t } = useTranslation();

  const draftBlockButtons = renderDraftButtons({
    ViewDraftButton,
    ResetDraftButton,
  });

  return (
    <div
      className="neeto-molecules-publish-block"
      data-testid="neeto-molecules-publish-block"
    >
      {isPresent(datetime) && (
        <Tooltip
          position="bottom"
          content={t("neetoMolecules.publishBlock.tooltip.publishLater", {
            date: dateFormat.dateTime(datetime),
          })}
        >
          <div>
            <Clock />
          </div>
        </Tooltip>
      )}
      {draftBlockButtons && (
        <>
          <div className="flex items-center gap-2">
            <Typography
              className="neeto-ui-text-gray-700"
              data-testid="publish-block-draft-label"
              lineHeight="normal"
              style="body2"
            >
              {t("neetoMolecules.common.actions.draft")}
            </Typography>
            <div className="flex items-center gap-1">{draftBlockButtons}</div>
          </div>
          <div className="neeto-ui-bg-gray-300 h-full w-px" />
        </>
      )}
      <div className="neeto-molecules-publish-block__action-btn">
        {renderPublishButtons({ PublishButton, PublishPreviewButton })}
      </div>
    </div>
  );
};

PublishBlock.Alert = AlertBlock;
PublishBlock.SchedulePane = PublishSchedulePane;

PublishBlock.propTypes = {
  /**
   * The function to render the draft buttons. It provides the `ViewDraftButton` and `ResetDraftButton` components as arguments.
   */
  renderDraftButtons: PropTypes.func,
  /**
   * The function to render the publish buttons. It provides the `PublishButton` and `PublishPreviewButton` components as arguments.
   */
  renderPublishButtons: PropTypes.func,
};
export default PublishBlock;
